import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { activitiesService } from '../services/activitiesService';
import type { ActivityFeedQuery, CreateActivityRequest, UpdateActivityRequest } from '@troia-v3/types';

/**
 * Hook para buscar atividades de um lead
 */
export const useLeadActivities = (leadId: string | null, query?: Partial<ActivityFeedQuery>) => {
  return useQuery({
    queryKey: ['lead-activities', leadId, query],
    queryFn: () => activitiesService.getLeadActivities(leadId!, query),
    enabled: !!leadId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook para adicionar atividade ao lead
 */
export const useAddLeadActivity = (leadId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Omit<CreateActivityRequest, 'entityType' | 'entityId' | 'category' | 'actorType' | 'actorId'>) =>
      activitiesService.addLeadActivity(leadId, data),
    onSuccess: () => {
      // Invalida cache das atividades do lead
      queryClient.invalidateQueries({ queryKey: ['lead-activities', leadId] });
    },
  });
};

/**
 * Hook para atualizar uma atividade
 */
export const useUpdateActivity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateActivityRequest }) =>
      activitiesService.updateActivity(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-activities'] });
      queryClient.invalidateQueries({ queryKey: ['my-activities'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
};

/**
 * Hook para excluir uma atividade
 */
export const useDeleteActivity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => activitiesService.deleteActivity(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-activities'] });
      queryClient.invalidateQueries({ queryKey: ['my-activities'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
};
